import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, RefreshCw } from 'lucide-react';
import { PageHeader } from '@/components/layout';
import { Button, Card, CardContent, TableSkeleton } from '@/components/ui';
import { CursorPagination } from '@/components/ui/cursor-pagination';
import { ApiErrorDisplay } from '@/components/features/error';
import { MerchantFilters } from '@/components/features/merchants/merchant-filters';
import { MerchantTable } from '@/components/features/merchants/merchant-table';
import { useMerchants } from '@/hooks/use-merchants';
import { useAuth } from '@/hooks/use-auth';
import type { ListMerchantsParams } from '@/types/merchant';

export function MerchantsPage() {
  const { t } = useTranslation('merchants');
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPlatformAdmin = user?.role === 'platform_admin';

  const [filters, setFilters] = useState<ListMerchantsParams>({ limit: 20 });
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const currentCursor = cursorStack.length > 0 ? cursorStack[cursorStack.length - 1] : undefined;

  const { data, isLoading, error, refetch } = useMerchants({
    ...filters,
    cursor: currentCursor,
  });

  const handleFiltersChange = useCallback((newFilters: ListMerchantsParams) => {
    setFilters(newFilters);
    setCursorStack([]);
  }, []);

  const handleNextPage = useCallback((cursor: string) => {
    setCursorStack((prev) => [...prev, cursor]);
  }, []);

  const handlePreviousPage = useCallback(() => {
    setCursorStack((prev) => prev.slice(0, -1));
  }, []);

  const handleRowClick = useCallback((merchantId: string) => {
    navigate(`/merchants/${merchantId}`);
  }, [navigate]);

  return (
    <div>
      <PageHeader
        title={t('list.title')}
        description={t('list.description')}
        actions={
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              <RefreshCw className="mr-2 h-4 w-4" />
              {t('common:button.refresh')}
            </Button>
            {isPlatformAdmin && (
              <Button size="sm" onClick={() => navigate('/merchants/new')}>
                <Plus className="mr-2 h-4 w-4" />
                {t('list.newMerchant')}
              </Button>
            )}
          </div>
        }
      />

      <MerchantFilters filters={filters} onFiltersChange={handleFiltersChange} />

      {isLoading && (
        <Card>
          <CardContent className="py-6">
            <TableSkeleton rows={5} cols={7} />
          </CardContent>
        </Card>
      )}

      {error && <ApiErrorDisplay error={error} onRetry={() => refetch()} />}

      {!isLoading && !error && data && (
        <Card>
          <MerchantTable
            merchants={data.data}
            onRowClick={handleRowClick}
          />
          <CursorPagination
            hasMore={data.pagination.hasMore}
            nextCursor={data.pagination.nextCursor}
            onNext={handleNextPage}
            onPrevious={handlePreviousPage}
            hasPrevious={cursorStack.length > 0}
            isLoading={isLoading}
          />
        </Card>
      )}
    </div>
  );
}
